import { useCallback, useEffect, useRef, useState } from 'react';

import { useNavigation } from '@react-navigation/native';

import { EntryInfoFragment } from '@/api/entries/query.generated';
import { CreateEntryTimeout } from '@/hooks/use-entries';
import { SubmitEntryNavigationProp } from '@/navigation/pickem/types';
import { GameMode } from '@/types/api.generated';
import { sleep } from '@/utils/promises';

import { MysteryWheelRef } from '../components/mystery-wheel/MysteryWheel';
import { BetslipPick } from '../types';
import { useBetslipStore } from './use-betslip-store';
import { usePlaceEntry } from './use-place-entry';
import { usePlaceEntryHandlers } from './use-place-entry-handlers';

const MINIMUM_SPIN_DURATION = 2400;

type UseSubmitEntryFlowProps = {
    picks: BetslipPick[];
    isMysteryMode: boolean;
    onInvalidProjectionsFound?: () => void;
    initialEntry?: EntryInfoFragment;
};

export const useSubmitEntryFlow = ({
    picks,
    isMysteryMode,
    onInvalidProjectionsFound,
    initialEntry,
}: UseSubmitEntryFlowProps) => {
    const minMysteryMultiplier = useBetslipStore(
        state => state.validationData[GameMode.Mystery].betValidation?.minMysteryMultiplier
    );

    const [entry, setEntry] = useState<EntryInfoFragment | undefined>(initialEntry);
    const [isTimeout, setIsTimeout] = useState(false);
    const keepBetDataWhenScreenRemoved = useRef(false);
    const [showWheel, setShowWheel] = useState(!initialEntry && isMysteryMode);
    const [wheelStopped, setWheelStopped] = useState(false);
    const [wheelExited, setWheelExited] = useState(false);
    const wheelRef = useRef<MysteryWheelRef>(null);
    const spinStartTimeRef = useRef<number | null>(null);

    const shouldShowConfetti = !!entry && entry.initialMultiplier > (minMysteryMultiplier ?? 2);

    const stopWheelSpin = useCallback((multiplier: number) => {
        wheelRef.current?.stopRotation(multiplier);
    }, []);

    const cancelWheelSpin = useCallback(() => {
        wheelRef.current?.cancelRotation();
    }, []);

    const startWheelSpin = useCallback(() => {
        if (wheelRef.current) {
            spinStartTimeRef.current = Date.now();
            wheelRef.current.startRotation();
        }
    }, []);

    const navigation = useNavigation<SubmitEntryNavigationProp>();

    const { handleSuccess, handleError } = usePlaceEntryHandlers({
        onInvalidProjectionsFound,
        isMysteryMode,
    });

    const onError = useCallback(
        (e: unknown) => {
            if (e instanceof CreateEntryTimeout) {
                if (isMysteryMode) setShowWheel(false);
                setIsTimeout(true);
                return;
            }
            keepBetDataWhenScreenRemoved.current = true;
            if (isMysteryMode) {
                cancelWheelSpin();
                handleError(e, () => {
                    setShowWheel(false);
                    navigation.goBack();
                });
            } else {
                handleError(e);
            }
        },
        [handleError, isMysteryMode, navigation, cancelWheelSpin]
    );

    const onSuccess = useCallback(
        async (newEntry: EntryInfoFragment) => {
            if (isMysteryMode) {
                const elapsed = Date.now() - (spinStartTimeRef.current ?? 0);
                const remaining = MINIMUM_SPIN_DURATION - elapsed;
                if (remaining > 0) {
                    await sleep(remaining);
                }
                stopWheelSpin(newEntry.initialMultiplier);
            }
            setEntry(newEntry);
            await handleSuccess(newEntry);
        },
        [handleSuccess, stopWheelSpin, isMysteryMode]
    );

    const { placeEntry, status } = usePlaceEntry({ picks, onSuccess, onError });

    const resetAndSpin = useCallback(
        (newAmount: number) => {
            setEntry(undefined);
            setShowWheel(true);
            setWheelStopped(false);
            setWheelExited(false);
            placeEntry(newAmount);
        },
        [placeEntry]
    );

    const handleWheelStop = useCallback(() => {
        setWheelStopped(true);
    }, []);

    const handleWheelExited = useCallback(() => {
        setWheelExited(true);
    }, []);

    // Hide the wheel after it stops, leaving extra time for the confetti animation when applicable.
    useEffect(() => {
        if (!wheelStopped) return;
        const timeout = setTimeout(() => setShowWheel(false), shouldShowConfetti ? 1500 : 600);
        return () => clearTimeout(timeout);
    }, [wheelStopped, shouldShowConfetti]);

    useEffect(() => {
        if (showWheel) {
            const timeout = setTimeout(() => {
                startWheelSpin();
            }, 900);
            return () => clearTimeout(timeout);
        }
    }, [showWheel, startWheelSpin]);

    return {
        entry,
        isTimeout,
        keepBetDataWhenScreenRemoved,
        operationStatus: status,
        shouldShowConfetti,
        showWheel,
        wheelRef,
        wheelStopped,
        wheelExited,
        handleWheelStop,
        handleWheelExited,
        resetAndSpin,
        placeEntry,
    };
};
